import { FC } from "react";
import { Card, Divider, Grid, Group, Stack } from "@mantine/core";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import Text from "@/components/atoms/Text";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { useAuth } from "@/contexts/AuthContext";
import { useGetOrderById } from "@/services/Orders";
import { OrderTimeSlot } from "@/types";
import styles from "./styles.module.css";

interface Props {
  order: OrderTimeSlot;
}

/** Order detail card shown inside a calendar day/slot popover — who is coming, when, and with what. */
export const OrderDetailCard: FC<Props> = ({ order }) => {
  const { user } = useAuth();
  const { t } = useTranslation();

  const { data: fullOrder } = useGetOrderById(order.orderId, undefined, undefined, {
    enabled: !!order.orderId,
  });

  const materialName =
    order.salesItem?.nameTranslated?.[user?.language?.id as number] ?? order.salesItem?.salesItem ?? "";

  return (
    <Card withBorder={false} padding="lg" radius={0} className={styles.detailCard}>
      <Grid p={0} align="center">
        <Grid.Col span={11} p={0}>
          <Text size="sm" strong className={styles.detailTitle}>
            {`${order.counterPartyName} - ${order.companySpecificproduct.companySpecificProductName} - ${materialName}`}
          </Text>
        </Grid.Col>
        <Grid.Col span={1} p={0}>
          <SVGAsset name={order.orderType?.type === "BUY" ? "purchaseIndicator" : "salesIndicator"} />
        </Grid.Col>
      </Grid>
      <Divider mt={12} />
      <Stack mt="sm" mb="sm" gap={3}>
        {order.transportCompanyName && (
          <Group justify="space-between">
            <Text size="xs" color="secondary">
              {t("order:transportCompany")}
            </Text>
            <Text size="xs" color="secondary">
              {order.transportCompanyName}
            </Text>
          </Group>
        )}
        <Group justify="space-between">
          <Text size="xs" color="secondary">
            {order.orderType?.type === "BUY" ? t("contracts:buyer") : t("contracts:seller")}
          </Text>
          <Text size="xs" color="secondary">
            {order.counterPartyName}
          </Text>
        </Group>
        <Group justify="space-between">
          <Text size="xs" color="secondary">
            {t("orders:transactionDate")}
          </Text>
          <Text size="xs" color="secondary">
            {dayjs(order.startDate).format("DD.MM.YYYY")}
          </Text>
        </Group>
        <Group justify="space-between">
          <Text size="xs" color="secondary">
            {t("orders:receptionSlots.timeLabel")}
          </Text>
          <Text size="xs" color="secondary">
            {dayjs(order.startDate).format("HH:mm")} - {dayjs(order.endDate).format("HH:mm")}
          </Text>
        </Group>
      </Stack>
      <Group justify="space-between" className={styles.detailFooter}>
        <Stack gap={0}>
          <Text size="xs" color="secondary">
            {t("common:amount")}
          </Text>
          <Text size="sm" strong>
            {fullOrder?.amount} {fullOrder?.unit.unit}
          </Text>
        </Stack>
        <Stack gap={0}>
          <Text size="xs" color="secondary">
            {t("product:material")}
          </Text>
          <Text size="sm" strong>
            {materialName}
          </Text>
        </Stack>
        <Stack gap={0}>
          <Text size="xs" color="secondary">
            {t("product:boxType")}
          </Text>
          <Text size="sm" strong>
            {t(`enumerables:boxTypes.${fullOrder?.packaging.boxtype.name ?? "undefined"}`)}
          </Text>
        </Stack>
      </Group>
    </Card>
  );
};

export default OrderDetailCard;
